// CARICAMENTO A BLOCCHI — lato browser (2026-09-13).
//
// Questo e' il pezzo che mancava: finora il formato LCB2 esisteva nel banco di
// prova e nel server, ma il prodotto non nominava nemmeno le rotte nuove.
//
// Tre cose vivono qui:
//   - il caricamento vero, a blocchi, con quattro in volo;
//   - la RIPRESA di un caricamento interrotto;
//   - lo scaricamento in flusso di un file scritto in questo formato.
//
// ── PERCHE' LA CHIAVE STA NELLA RIGA DI SESSIONE ────────────────────────────
//
// Riprendere e' facile da fare male. La versione ingenua chiama generaCek() di
// nuovo, e allora i blocchi vecchi e quelli nuovi finiscono cifrati con due
// chiavi diverse. Il file si assembla, la riga nasce, il vault lo mostra: e
// non si apre a meta'. E' il peggiore dei guasti, perche' si scopre tardi.
// Percio' la CEK incartata viene salvata nella riga di sessione all'apertura,
// e chi riprende scarta quella — mai una nuova.
//
// ── PERCHE' LA SCELTA E' SEMPRE DELL'UTENTE ─────────────────────────────────
//
// Una sessione aperta si riconosce da nome e dimensione, e due file diversi
// possono coincidere su entrambi. Quindi non si riprende mai da soli: si dice
// che c'e' un caricamento a meta' e si lascia scegliere.
import {
  DIMENSIONE_BLOCCO, LUNGHEZZA_INTESTAZIONE, LUNGHEZZA_IV, LUNGHEZZA_TAG,
  generaCek, incartaCek, apriCek, creaIntestazione, leggiIntestazione,
  cifraBlocco, decifraBlocco,
} from './cifraturaBlocchi';
import { intestazioniAuth, BASE_API } from './api';

// La CEK incartata sono BYTE (60), e nel JSON i byte non ci stanno: un
// Uint8Array passato a JSON.stringify diventa {"0":12,"1":...} e il server si
// ritrova un oggetto al posto di una stringa. Il 13 settembre questo dava un
// 500 all'apertura della sessione. Sul filo e nella riga viaggia in base64;
// dentro l'intestazione del blob restano i byte veri.
const aB64 = (u8) => btoa(String.fromCharCode(...u8));
const daB64 = (t) => Uint8Array.from(atob(t), (c) => c.charCodeAt(0));

const IN_VOLO = 4;                 // misurato: 6,4 MB/s contro 1,8 in fila
const RADICE = () => `${BASE_API}/files-blocchi`;

const json = async (r) => { try { return await r.json(); } catch { return {}; } };
function errore(dati, r) {
  return Object.assign(new Error(dati.error || `Errore ${r.status}`), {
    code: dati.code, status: r.status, maxFileSize: dati.maxFileSize,
    // quotaBytes: la dimensione del piano, che il server manda insieme al
    // rifiuto per spazio esaurito. Senza, il messaggio puo' solo ripetere il
    // testo inglese del server.
    quotaBytes: dati.quotaBytes, usedBytes: dati.usedBytes,
    maxFiles: dati.maxFiles, fileCount: dati.fileCount,
  });
}

// ── L'INTERRUTTORE ──────────────────────────────────────────────────────────
//
// Non c'e' un secondo interruttore nel frontend da tenere allineato a mano:
// si chiede al server. Se BLOCCHI_ATTIVI non e' acceso le rotte non sono
// nemmeno montate, la richiesta torna 404 e il vault continua come sempre.
// Una sola manopola, sul server, come voluto.
let promessaDisponibile = null;
export function blocchiDisponibili() {
  if (!promessaDisponibile) {
    promessaDisponibile = fetch(`${RADICE()}/sessioni`, { headers: intestazioniAuth() })
      .then((r) => r.ok)
      .catch(() => false);
  }
  return promessaDisponibile;
}
export function dimenticaDisponibilita() { promessaDisponibile = null; }

// ── LE SESSIONI APERTE ──────────────────────────────────────────────────────
export async function sessioniAperte() {
  const r = await fetch(`${RADICE()}/sessioni`, { headers: intestazioniAuth() });
  if (!r.ok) return [];
  return json(r);
}

// Stesso nome E stessa dimensione. Non basta a dire che e' lo stesso file —
// per questo si chiede — ma basta a non proporre sciocchezze.
export function sessioneCombaciante(file, sessioni, folderId) {
  return (sessioni || []).find((s) =>
    s.plainName === file.name &&
    Number(s.size) === file.size &&
    (s.folderId || null) === (folderId || null) &&
    s.mancanti > 0) || null;
}

export async function abbandonaSessione(sessionId) {
  try { await fetch(`${RADICE()}/sessione/${sessionId}`, { method: 'DELETE', headers: intestazioniAuth() }); } catch {}
}

async function bloccchiMancanti(sessionId) {
  const r = await fetch(`${RADICE()}/sessione/${sessionId}`, { headers: intestazioniAuth() });
  if (!r.ok) throw errore(await json(r), r);
  return json(r);
}

// ── IL CARICAMENTO ──────────────────────────────────────────────────────────
//
// `ripresa` e' la riga di sessione restituita da sessioniAperte(): se c'e', si
// continua quella — stessa sessione, stessa CEK, solo i blocchi che mancano.
// IL CORPO DELLA PUT VIAGGIA IN STREAMING, QUANDO IL BROWSER LO SA FARE
// (2026-09-14). Il fatto, misurato senza DevTools attaccato: con il corpo
// intero (Uint8Array) Chrome tiene una copia di ogni blocco spedito finche'
// non passa un garbage collector, e mentre si carica la pagina non e' mai
// inattiva, quindi il GC non passa — un file da 4 GB portava il renderer a
// 1,9 GB e l'OOM lo uccideva in trenta secondi. Con un ReadableStream il
// browser spedisce i pezzi man mano e non conserva niente: 400 MB caricati,
// picco 301 MB contro 926. Serve HTTP/2 (nginx ce l'ha) e `duplex: 'half'`.
// Dove i flussi in salita non esistono (Safari) si torna al corpo intero:
// il controllo si fa una volta e vale per tutta la sessione.
const PEZZO_FLUSSO = 256 * 1024;
let flussoInSalita = null;
function supportaFlussoInSalita() {
  if (flussoInSalita !== null) return flussoInSalita;
  // IL CONTROLLO GIUSTO, NON "SE NON LANCIA VA BENE" (2026-09-14, un'ora dopo
  // la prima versione). Firefox NON lancia con un ReadableStream come corpo:
  // lo converte in testo e spedisce ventitre' byte, "[object ReadableStream]",
  // rispondendo 200 — corruzione silenziosa. Il controllo canonico (quello dei
  // Chrome developers) guarda due cose: che il costruttore abbia LETTO
  // `duplex` (lo fa solo chi implementa i flussi in salita) e che NON abbia
  // messo un Content-Type testuale (lo mette chi ha convertito il corpo in
  // stringa). Tutte e due, altrimenti e' no.
  try {
    let duplexLetto = false;
    const r = new Request('https://x/', {
      method: 'POST', body: new ReadableStream(),
      get duplex() { duplexLetto = true; return 'half'; },
    });
    flussoInSalita = duplexLetto && !r.headers.has('Content-Type');
  } catch { flussoInSalita = false; }
  return flussoInSalita;
}
// Cartella OPFS separata da quella dello scaricamento: cosi' lo spazzino dei
// file di lavoro non tocca un blocco che sta salendo, e viceversa.
const CARTELLA_SALITA = 'caricamenti-in-corso';

// COME VIAGGIA IL CORPO DI OGNI BLOCCO. Tre strade, in ordine di preferenza:
//   1. FLUSSO IN SALITA (Chrome, Edge; Safari solo da STP 250): un
//      ReadableStream spedito a pezzi, niente resta in memoria.
//   2. FILE OPFS (Safari e Firefox di oggi, che i flussi non li hanno): il
//      blocco cifrato si scrive in un file OPFS temporaneo e si spedisce
//      QUELLO. Misurato su WebKit vero: 400 MB con il corpo Uint8Array in
//      memoria trattengono +1,4 GB e il browser muore in OOM; lo stesso corpo
//      come File su disco trattiene +18 MB. E' la cura per il tetto a 5 GB su
//      quei browser. Il file si cancella appena la PUT e' finita.
//   3. CORPO IN MEMORIA (browser senza flussi in salita E senza OPFS: molto
//      raro): l'unica strada rimasta. Su file grandi puo' esaurire la memoria;
//      non c'e' modo migliore senza OPFS.
async function corpoDaSpedire(corpo, indice) {
  if (supportaFlussoInSalita()) {
    let pos = 0;
    const body = new ReadableStream({
      pull(c) {
        if (pos >= corpo.length) { c.close(); return; }
        c.enqueue(corpo.subarray(pos, Math.min(pos + PEZZO_FLUSSO, corpo.length)));
        pos += PEZZO_FLUSSO;
      },
    });
    return { opts: { body, duplex: 'half' }, pulisci: null };
  }
  if (opfsDisponibile()) {
    try {
      const dir = await (await navigator.storage.getDirectory()).getDirectoryHandle(CARTELLA_SALITA, { create: true });
      const nome = 'blk-' + Date.now() + '-' + indice + '-' + Math.random().toString(36).slice(2, 6);
      const manico = await dir.getFileHandle(nome, { create: true });
      const scrittore = await manico.createWritable();
      await scrittore.write(corpo);
      await scrittore.close();
      const file = await manico.getFile();
      return { opts: { body: file }, pulisci: () => dir.removeEntry(nome).catch(() => {}) };
    } catch { /* OPFS negato o pieno: si ripiega al corpo in memoria */ }
  }
  return { opts: { body: corpo }, pulisci: null };
}

export async function caricaABlocchi(file, chiaveUtente, opzioni = {}) {
  // `onSessione` serve all'Annulla: chi sta in coda deve sapere QUALE sessione
  // fermare, e deve saperlo appena si apre — non a caricamento finito. Senza,
  // premere Annulla fermerebbe il browser e lascerebbe i blocchi sul server.
  const { folderId = null, ripresa = null, avanzamento = null, segnale = null, onSessione = null } = opzioni;

  let sessione, cekIncartata, daFare;
  if (ripresa) {
    if (!ripresa.cekIncartata) {
      // Una sessione senza chiave e' nata prima di questa riga di codice. Non
      // si riprende alla cieca: si dice che non si puo'.
      throw Object.assign(new Error('Sessione senza chiave'), { code: 'SESSIONE_SENZA_CHIAVE' });
    }
    sessione = { sessionId: ripresa.sessionId, totaleBlocchi: ripresa.totaleBlocchi };
    cekIncartata = daB64(ripresa.cekIncartata);
    // I mancanti si rileggono adesso: quelli nella lista possono essere vecchi
    // di minuti, e il disco e' l'unica verita'.
    daFare = (await bloccchiMancanti(ripresa.sessionId)).mancanti;
  } else {
    const cek0 = await generaCek();
    cekIncartata = await incartaCek(cek0, chiaveUtente);
    const r = await fetch(`${RADICE()}/sessione`, {
      method: 'POST', headers: { ...intestazioniAuth(), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        plainName: file.name, size: file.size, blockSize: DIMENSIONE_BLOCCO,
        folderId: folderId || null, mimeType: file.type || 'application/octet-stream',
        cekIncartata: aB64(cekIncartata),
      }),
    });
    const dati = await json(r);
    if (!r.ok) throw errore(dati, r);
    sessione = dati;
    daFare = Array.from({ length: dati.totaleBlocchi }, (_, i) => i);
  }

  if (onSessione) onSessione(sessione.sessionId);

  // La CEK e' SEMPRE quella incartata nella sessione, anche al primo giro:
  // cosi' il caricamento nuovo e la ripresa percorrono la stessa strada e non
  // esiste un ramo provato meno dell'altro.
  const cek = await apriCek(cekIncartata, chiaveUtente);
  const totale = sessione.totaleBlocchi;
  const gia = totale - daFare.length;
  let fatti = 0;
  // `extra` porta la fase: durante l'assemblaggio sul server il pannello deve
  // dire quello, non "100% · qualche secondo" per minuti.
  const riporta = (extra = {}) => avanzamento && avanzamento({ fatti: gia + fatti, totale, ripresa: !!ripresa, ...extra });
  riporta();

  let prossimo = 0, guasto = null;
  async function unBlocco(i) {
    const a = i * DIMENSIONE_BLOCCO;
    const z = Math.min(a + DIMENSIONE_BLOCCO, file.size);
    const pezzo = new Uint8Array(await file.slice(a, z).arrayBuffer());
    const cifrato = await cifraBlocco(cek, pezzo, i, totale, file.size);
    let corpo = cifrato;
    if (i === 0) {
      // L'intestazione viaggia davanti al blocco 0: tutto quello che serve ad
      // aprire il file sta dentro il file, mai solo in una riga di database.
      const testa = creaIntestazione({ dimensioneBlocco: DIMENSIONE_BLOCCO, dimensioneInChiaro: file.size, cekIncartata });
      corpo = new Uint8Array(testa.length + cifrato.length);
      corpo.set(testa, 0); corpo.set(cifrato, testa.length);
    }
    // UN SEGNALE PER BLOCCO, NON QUELLO DELL'INTERO FILE (2026-09-14).
    // Passare lo stesso AbortSignal a mille fetch lascia mille ascoltatori
    // appesi a quel segnale, e ognuno tiene vivo il corpo della sua richiesta
    // — 4 MB di blocco cifrato — finche' il segnale non muore, cioe' a fine
    // file. Misurato: un caricamento da 4 GB portava il browser a 1,9 GB di
    // memoria anonima e l'OOM lo uccideva al terzo minuto. Qui ogni blocco ha
    // il suo controller, l'annullamento del file gli viene inoltrato, e
    // l'inoltro si toglie appena il blocco e' finito: niente resta appeso.
    const mio = new AbortController();
    const inoltra = () => mio.abort();
    if (segnale) {
      if (segnale.aborted) mio.abort();
      else segnale.addEventListener('abort', inoltra, { once: true });
    }
    const { opts, pulisci } = await corpoDaSpedire(corpo, i);
    try {
      const r = await fetch(`${RADICE()}/sessione/${sessione.sessionId}/${i}`, {
        method: 'PUT', headers: { ...intestazioniAuth(), 'Content-Type': 'application/octet-stream' },
        ...opts, signal: mio.signal,
      });
      if (!r.ok) throw errore(await json(r), r);
      // LA RISPOSTA SI LEGGE FINO IN FONDO, ANCHE SE NON SERVE (2026-09-14).
      // Una Response lasciata a meta' tiene in piedi il suo caricatore, e il
      // caricatore tiene i 4 MB del corpo appena spedito finche' il garbage
      // collector non passa: misurato, il renderer saliva di un blocco per
      // blocco fino a 1,7 GB. Consumarla chiude tutto subito.
      await r.arrayBuffer();
    } finally {
      if (segnale) segnale.removeEventListener('abort', inoltra);
      // Il file OPFS del blocco (se c'era) si toglie subito: e' servito solo a
      // tenere il corpo fuori dalla memoria durante la PUT.
      if (pulisci) await pulisci();
    }
    fatti++; riporta();
  }
  async function operaio() {
    while (!guasto) {
      const i = prossimo++;
      if (i >= daFare.length) return;
      try { await unBlocco(daFare[i]); } catch (e) { guasto = guasto || e; return; }
    }
  }
  await Promise.all(Array.from({ length: Math.min(IN_VOLO, daFare.length || 1) }, () => operaio()));
  if (guasto) {
    // Non si cancella niente: i blocchi arrivati restano, e sono esattamente
    // quelli che permettono di riprendere. Se l'utente non torna, li toglie lo
    // spazzino dopo sette giorni.
    guasto.sessionId = sessione.sessionId;
    throw guasto;
  }

  // LA CHIUSURA E' ASINCRONA (2026-09-14): il server risponde 202 e incolla
  // i blocchi per conto suo; qui si interroga finche' non e' 'chiusa'. Per un
  // file da 49 GB l'assemblaggio dura minuti, e una richiesta che aspettava
  // moriva al timeout. Se il server risponde subito con la riga (201/200), e'
  // la strada vecchia o una sessione gia' chiusa: va bene lo stesso.
  return chiudiEAspetta(sessione.sessionId, segnale, riporta);
}

async function chiediChiusura(sessionId, segnale) {
  const c = await fetch(`${RADICE()}/sessione/${sessionId}/chiudi`, {
    method: 'POST', headers: { ...intestazioniAuth(), 'Content-Type': 'application/json' }, body: '{}',
    signal: segnale || undefined,
  });
  const dati = await json(c);
  if (!c.ok) throw errore(dati, c);
  const inLavorazione = ['chiusura', 'in_coda', 'in_attesa', 'in_assemblaggio'].includes(dati?.stato);
  return { inChiusura: c.status === 202 || inLavorazione, riga: dati };
}

// Traduce lo stato del server nella fase che il pannello sa mostrare: coda con
// posizione, attesa di spazio, o assemblaggio vero e proprio.
function faseDaStato(d) {
  if (d?.stato === 'in_coda') return { fase: 'coda', posizione: d.posizione || 0 };
  if (d?.stato === 'in_attesa') return { fase: 'attesa-spazio', posizione: d.posizione || 0 };
  return { fase: 'assemblaggio' };
}

function pausa(ms, segnale) {
  return new Promise((ris, rif) => {
    const t = setTimeout(ris, ms);
    if (segnale) segnale.addEventListener('abort', () => { clearTimeout(t); rif(new DOMException('Annullato', 'AbortError')); }, { once: true });
  });
}

async function chiudiEAspetta(sessionId, segnale, riporta) {
  let { inChiusura, riga } = await chiediChiusura(sessionId, segnale);
  if (!inChiusura) return riga;
  if (riporta) riporta(faseDaStato(riga));
  const inizio = Date.now();
  let richieste = 1;
  // Sei ore: e' il tetto per un assemblaggio da decine di GB sulla Storage
  // Box, non un'attesa normale. Se scade, qualcosa non va davvero.
  while (Date.now() - inizio < 6 * 60 * 60 * 1000) {
    await pausa(3000, segnale);
    const r = await fetch(`${RADICE()}/sessione/${sessionId}`, { headers: intestazioniAuth(), signal: segnale || undefined });
    const d = await json(r);
    if (!r.ok) throw errore(d, r);
    if (d.stato === 'chiusa' && d.file) return d.file;
    if (d.stato === 'aperta') {
      // L'assemblaggio e' fallito e il server ha riaperto la sessione: si
      // richiede UNA volta. Al secondo fallimento si dice, non si insiste.
      if (++richieste > 2) throw Object.assign(new Error('Assemblaggio fallito'), { code: 'ASSEMBLAGGIO', sessionId });
      ({ inChiusura, riga } = await chiediChiusura(sessionId, segnale));
      if (!inChiusura) return riga;
    }
    if (riporta) riporta(faseDaStato(d));
  }
  throw Object.assign(new Error('Chiusura troppo lunga'), { code: 'CHIUSURA_LENTA', sessionId });
}

// ── IL FILE DI LAVORO SUL DISCO DEL BROWSER (OPFS) ──────────────────────────
//
// Dove showSaveFilePicker non c'e' — Firefox, Safari, e Chrome quando l'utente
// annulla la finestra di salvataggio — i pezzi decifrati non possono restare
// in memoria: il 13 settembre un file da 4 GB ha fatto uccidere la scheda dal
// sistema, 4,56 GB di RAM in una sola pagina. Si scrivono invece in un file di
// lavoro nello spazio privato del sito (OPFS), che esiste su tutti e tre i
// browser, e alla fine si consegna QUELLO: il Blob che ne esce sta su disco,
// non in RAM.
//
// ── IL FILE ESISTE DUE VOLTE, E PER POCO ────────────────────────────────────
// Durante la consegna il contenuto sta sul disco dell'utente due volte: nel
// file di lavoro e nella copia che il browser sta salvando. Non c'e' modo di
// evitarlo per questa strada — per questo il file di lavoro si cancella
// SEMPRE, e in tre modi diversi, perche' uno solo non basta:
//   1. appena la consegna e' partita e il browser ha finito di leggerlo;
//   2. subito, se qualcosa va storto o l'utente annulla;
//   3. se la pagina si chiude a meta' (pagehide), e comunque alla partenza
//      dello scaricamento successivo, che spazza via quello che fosse
//      sopravvissuto a un blackout.
// La regola e' che nello spazio privato non deve restare niente di nostro
// quando non si sta scaricando.
const CARTELLA_LAVORO = 'scarichi-in-corso';
const PREFISSO_LAVORO = 'lavoro-';

// ── COME SI SA SE UN FILE DI LAVORO SERVE ANCORA ────────────────────────────
//
// La cancellazione alla chiusura della pagina non basta, e l'ho verificato:
// removeEntry e' asincrona, e una scheda che muore non aspetta che finisca.
// Un file da 300 MB e' sopravvissuto a una chiusura pulita.
//
// Quindi lo scaricamento in corso batte un colpo ogni dieci secondi. Chi
// spazza guarda l'ultimo colpo: se e' vecchio di piu' di un minuto, quella
// scheda non c'e' piu' e il file non serve a nessuno. Un minuto, non un'ora:
// il file di lavoro e' grande quanto il file scaricato, e lasciarlo sul disco
// di un cliente e' occupare spazio suo per comodita' nostra.
//
// Il registro dei colpi sta in localStorage perche' deve sopravvivere alla
// morte della scheda. Puo' LANCIARE — nel browser interno di Facebook
// succede — quindi ogni lettura e scrittura e' protetta: se non funziona si
// resta senza registro e si spazza col criterio dell'ora, mai si rompe lo
// scaricamento.
const CHIAVE_VIVI = 'lc-scarichi-vivi';
const COLPO_MS = 10 * 1000;
const SENZA_COLPI_MS = 60 * 1000;
const VECCHIO_MS = 60 * 60 * 1000;   // rete di sicurezza, se il registro non c'e'

function leggiVivi() {
  try { return JSON.parse(localStorage.getItem(CHIAVE_VIVI) || '{}') || {}; } catch { return {}; }
}
function scriviVivi(v) {
  try { localStorage.setItem(CHIAVE_VIVI, JSON.stringify(v)); } catch {}
}
function batti(nome) {
  const v = leggiVivi(); v[nome] = Date.now(); scriviVivi(v);
}
function nonBattePiu(nome) {
  const v = leggiVivi(); delete v[nome]; scriviVivi(v);
}

const opfsDisponibile = () => !!(navigator.storage && navigator.storage.getDirectory);

async function cartellaLavoro(crea = true) {
  const radice = await navigator.storage.getDirectory();
  return radice.getDirectoryHandle(CARTELLA_LAVORO, { create: crea });
}

// Spazzino: toglie i file di lavoro rimasti da sessioni morte male. Non tocca
// quelli nati da poco, perche' potrebbe esserci un altro scaricamento in corso
// in un'altra scheda.
export async function spazzaFileDiLavoro() {
  if (!opfsDisponibile()) return 0;
  // I blocchi di salita si cancellano da soli a PUT finita; se una scheda
  // muore a meta' ne resta qualcuno. Si toglie l'intera cartella: quando
  // questo gira, nessun caricamento e' in corso in questa scheda.
  try { await (await navigator.storage.getDirectory()).removeEntry(CARTELLA_SALITA, { recursive: true }); } catch {}
  let tolti = 0;
  try {
    // create:false — lo spazzino gira all'apertura del vault per TUTTI, anche
    // con l'interruttore spento e anche per chi non ha mai scaricato niente a
    // blocchi. Non deve lasciare un segno del suo passaggio: se la cartella
    // non c'e', non la si crea, si esce e basta.
    const dir = await cartellaLavoro(false);
    const ora = Date.now();
    const vivi = leggiVivi();
    let rimasti = 0;
    for await (const [nome, manico] of dir.entries()) {
      if (!nome.startsWith(PREFISSO_LAVORO)) { rimasti++; continue; }
      try {
        const colpo = vivi[nome];
        // Un colpo recente vuol dire che c'e' una scheda che sta scaricando
        // proprio quello, magari un'altra: non si tocca.
        if (colpo && ora - colpo < SENZA_COLPI_MS) { rimasti++; continue; }
        // Senza registro (localStorage che lancia, o pulito dall'utente) resta
        // il criterio del tempo, piu' lento ma che non cancella mai qualcosa
        // di vivo.
        if (!colpo) {
          const f = await manico.getFile();
          if (ora - f.lastModified < VECCHIO_MS) { rimasti++; continue; }
        }
        await dir.removeEntry(nome);
        nonBattePiu(nome);
        tolti++;
      } catch { rimasti++; }
    }
    // La cartella vuota se ne va con gli altri: lo spazio privato dell'utente
    // deve tornare com'era, senza un ricordo del nostro passaggio.
    if (!rimasti) { try { await (await navigator.storage.getDirectory()).removeEntry(CARTELLA_LAVORO); } catch {} }
  } catch {}
  return tolti;
}

// ── LO SCARICAMENTO IN FLUSSO ───────────────────────────────────────────────
//
// Si legge dal flusso, si decifra un blocco per volta e lo si passa avanti:
// non si tiene mai in memoria piu' di un blocco. Dove il browser lo consente
// (showSaveFilePicker) i pezzi vanno direttamente sul disco e la memoria resta
// piatta; dove non lo consente si accumulano le parti in un Blob — Chrome le
// travasa su disco da solo, ma e' onesto dire che non e' la stessa cosa.
export async function scaricaABlocchi(fileId, nome, chiaveUtente, avanzamento = null, segnale = null) {
  // Prima di tutto si spazza: se una sessione precedente e' morta male, il suo
  // file di lavoro se ne va adesso e non aspetta di essere dimenticato.
  spazzaFileDiLavoro();

  // ── LA FINESTRA DI SALVATAGGIO SI APRE SUBITO ──────────────────────────
  //
  // Prima stava dopo la richiesta al server, e sembrava innocuo. Non lo e':
  // showSaveFilePicker si puo' chiamare solo mentre il clic dell'utente e'
  // ancora "caldo" — pochi secondi — e in mezzo c'era un giro di rete. Nelle
  // giornate storte di questo sito una richiesta ha impiegato 88 secondi: in
  // quel caso la finestra veniva rifiutata con AbortError, che il codice
  // leggeva come "l'utente ha annullato", e lo scaricamento moriva in
  // silenzio. L'utente preme Scarica e non succede niente, senza un perche'.
  // Adesso si chiede la destinazione PRIMA di chiedere il file.
  //
  // navigator.userActivation dice se il clic e' ancora valido: se non lo e',
  // non si prova nemmeno — si va dritti al file di lavoro, che non chiede
  // niente a nessuno. Cosi' l'unico AbortError possibile e' quello vero,
  // l'utente che chiude la finestra, e a quello si obbedisce.
  let scrittore = null, annullato = false;
  const clicCaldo = navigator.userActivation ? navigator.userActivation.isActive : true;
  if (window.showSaveFilePicker && clicCaldo) {
    try {
      const manico = await window.showSaveFilePicker({ suggestedName: nome });
      scrittore = await manico.createWritable();
    } catch (e) {
      if (e && e.name === 'AbortError') annullato = true;  // ha detto di no
      scrittore = null;
    }
  }
  if (annullato) return false;   // non si scarica di nascosto

  const r = await fetch(`${BASE_API}/files/${fileId}/download`, { headers: intestazioniAuth(), signal: segnale || undefined });
  if (!r.ok) throw errore(await json(r), r);

  const lettore = r.body.getReader();
  let coda = new Uint8Array(0);
  const prendi = async (quanti) => {
    while (coda.length < quanti) {
      const { value, done } = await lettore.read();
      if (done) break;
      const n = new Uint8Array(coda.length + value.length);
      n.set(coda, 0); n.set(value, coda.length); coda = n;
    }
    if (coda.length < quanti) return null;
    const fuori = coda.slice(0, quanti);
    coda = coda.slice(quanti);
    return fuori;
  };

  const testa = await prendi(LUNGHEZZA_INTESTAZIONE);
  if (!testa) throw new Error('File troncato: manca l\'intestazione');
  const h = leggiIntestazione(testa);
  const cek = await apriCek(h.cekIncartata, chiaveUtente);

  // ── dove si scrive ────────────────────────────────────────────────────
  // La destinazione e' gia' stata scelta qui sopra, prima della rete. Se
  // l'utente non ha una finestra di salvataggio, si passa al file di lavoro.
  let dir = null, nomeLavoro = null, manicoLavoro = null, sganciaChiusura = null, cuore = null;
  if (!scrittore) {
    if (!opfsDisponibile()) {
      // Nessuna delle due strade: si tiene in memoria, ma solo se e' poco. Un
      // file grosso qui ammazzerebbe la scheda, e vale la pena dirlo invece di
      // provarci.
      if (h.dimensioneInChiaro > 512 * 1024 * 1024) {
        try { lettore.cancel(); } catch {}
        throw Object.assign(new Error('Browser senza spazio di lavoro'), { code: 'SCARICO_NON_SUPPORTATO' });
      }
    } else {
      dir = await cartellaLavoro();
      nomeLavoro = PREFISSO_LAVORO + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
      manicoLavoro = await dir.getFileHandle(nomeLavoro, { create: true });
      scrittore = await manicoLavoro.createWritable();
      // Se la pagina muore adesso, il file di lavoro non sopravvive alla
      // chiusura: e' il terzo dei tre modi.
      batti(nomeLavoro);
      cuore = setInterval(() => batti(nomeLavoro), COLPO_MS);
      // pagehide resta, come primo tentativo: quando fa in tempo, pulisce
      // subito. Quando non fa in tempo — ed e' il caso normale, perche'
      // removeEntry e' asincrona — ci pensa lo spazzino, che dopo un minuto
      // senza colpi sa che quella scheda non c'e' piu'.
      const allaChiusura = () => { try { dir.removeEntry(nomeLavoro); } catch {} };
      window.addEventListener('pagehide', allaChiusura);
      sganciaChiusura = () => window.removeEventListener('pagehide', allaChiusura);
    }
  }
  const buttaIlLavoro = async () => {
    if (cuore) { clearInterval(cuore); cuore = null; }
    if (sganciaChiusura) sganciaChiusura();
    if (nomeLavoro) nonBattePiu(nomeLavoro);
    if (dir && nomeLavoro) { try { await dir.removeEntry(nomeLavoro); } catch {} }
    // Se non e' rimasto niente, via anche la cartella.
    try {
      let vuota = true;
      for await (const _ of dir.entries()) { vuota = false; break; }
      if (vuota) await (await navigator.storage.getDirectory()).removeEntry(CARTELLA_LAVORO);
    } catch {}
  };

  const parti = scrittore ? null : [];
  try {
    for (let i = 0; i < h.totaleBlocchi; i++) {
      const restante = h.dimensioneInChiaro - i * h.dimensioneBlocco;
      const quanti = LUNGHEZZA_IV + Math.min(restante, h.dimensioneBlocco) + LUNGHEZZA_TAG;
      const blocco = await prendi(quanti);
      if (!blocco) throw new Error(`File troncato: manca il blocco ${i} di ${h.totaleBlocchi}`);
      const chiaro = await decifraBlocco(cek, blocco, i, h.totaleBlocchi, h.dimensioneInChiaro);
      if (scrittore) await scrittore.write(chiaro); else parti.push(chiaro);
      if (avanzamento) {
        // In BYTE, non in blocchi: la riga mostra una percentuale e il totale
        // somma byte con gli altri trasferimenti. L'ultimo blocco e' piu'
        // corto, quindi si usa quanto e' stato davvero decifrato.
        avanzamento({
          scaricati: Math.min(h.dimensioneInChiaro, (i + 1) * h.dimensioneBlocco),
          totale: h.dimensioneInChiaro,
        });
      }
    }
    if (scrittore) await scrittore.close();
  } catch (e) {
    // Secondo modo: si rompe qualcosa, il file di lavoro se ne va subito.
    if (scrittore) { try { await scrittore.abort(); } catch {} }
    await buttaIlLavoro();
    throw e;
  }

  // Il file scelto dall'utente e' gia' scritto: non c'e' niente da consegnare
  // e niente da cancellare.
  if (!manicoLavoro && scrittore) return true;

  const contenuto = manicoLavoro ? await manicoLavoro.getFile() : new Blob(parti);
  const url = URL.createObjectURL(contenuto);
  const a = document.createElement('a');
  a.href = url; a.download = nome;
  document.body.appendChild(a); a.click(); a.remove();

  // Primo modo, e il piu' delicato: il file di lavoro non si puo' togliere
  // mentre il browser lo sta ancora leggendo, o lo scaricamento si spezzerebbe
  // a meta'. Il browser non avvisa quando ha finito — un blob non ha un evento
  // di fine — quindi si aspetta. Quanto, pero', non puo' essere un numero
  // fisso: e' una copia da disco a disco, e il tempo cresce col file. Si tiene
  // un minimo di due minuti piu' un secondo ogni 5 MB — su 4 GB fa quasi
  // quindici minuti. E' volutamente largo: sbagliare per eccesso significa un
  // file di lavoro che resta qualche minuto di troppo e poi se ne va;
  // sbagliare per difetto significa uno scaricamento spezzato a meta'.
  // Misurato il 13/09 su questa macchina: 100 MB consegnati in 3 secondi, 4 GB
  // in 100 secondi dal clic (decifratura compresa). Il margine e' ampio.
  // Se la pagina si chiude prima, ci pensa pagehide; se salta la corrente, lo
  // spazzino al prossimo scaricamento. Nessuna delle tre strade da sola basta.
  const attesa = 120000 + (h.dimensioneInChiaro / (5 * 1024 * 1024)) * 1000;
  setTimeout(async () => {
    URL.revokeObjectURL(url);
    await buttaIlLavoro();
  }, attesa);
  return true;
}
